import { Button, Group, Modal, Stack, Text, Textarea } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { i18n } from "@dehub/theme"
import dayjs from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { DeliveryDateChangeApi } from "../../apis"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"

// The server is the authority on whether a change may be asked for; these
// translate its refusals so a buyer is never shown DELIVERY_NOTE_STATUS and
// left to guess.
const REFUSAL_MESSAGES: Record<string, string> = {
  ORDER_STATUS       : "dateChange.refusal.orderStatus",
  DELIVERY_NOTE_STATUS: "dateChange.refusal.deliveryNoteStatus",
  DATE_IN_PAST       : "dateChange.refusal.dateInPast",
  DATE_UNCHANGED     : "dateChange.refusal.dateUnchanged",
}

function humanise(raw?: string): string {
  if (!raw) return i18n.t("buyerApp:common.genericError")
  if (raw.includes("date_change_already_pending")) {
    return i18n.t("buyerApp:dateChange.refusal.pending")
  }
  const refused = raw.split("error.order.date_change_refused:")[1]
  if (refused) {
    const code = refused.trim().split(/[^A-Z_]/)[0]
    const key = REFUSAL_MESSAGES[code]
    return key ? i18n.t(`buyerApp:${key}`) : i18n.t("buyerApp:dateChange.refusal.generic")
  }
  return raw
}

type Props = {
  opened: boolean
  onClose: () => void
  orderId: string
  currentDeliveryDate?: string | null
  onDone?: () => void
}

export function DeliveryDateChangeRequestModal({
  opened, onClose, orderId, currentDeliveryDate, onDone,
}: Props) {
  const { t } = useTranslation("buyerApp")
  // DatePickerInput hands back a YYYY-MM-DD string in this Mantine version.
  const [date, setDate] = useState<string | null>(null)
  const [reason, setReason] = useState("")
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!date) return
    try {
      setBusy(true)
      await DeliveryDateChangeApi.create(orderId, {
        requestedDeliveryDate: dayjs(date).toISOString(),
        reason               : reason.trim() || null,
      })
      message.success(t("dateChange.success"))
      setDate(null)
      setReason("")
      onClose()
      onDone?.()
    } catch (err) {
      message.error(humanise((err as HttpHandler)?.message))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("dateChange.title")}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t("dateChange.currentLabel", { date: currentDeliveryDate ? currentDeliveryDate.slice(0, 10) : "—" })}
        </Text>
        <DatePickerInput
          label={t("dateChange.newDateLabel")}
          placeholder={t("dateChange.newDatePlaceholder")}
          value={date}
          onChange={setDate}
          minDate={dayjs().format("YYYY-MM-DD")}
        />
        <Textarea
          label={t("dateChange.reasonLabel")}
          placeholder={t("dateChange.reasonPlaceholder")}
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t("common.cancel")}</Button>
          <Button loading={busy} disabled={!date} onClick={submit}>{t("dateChange.submit")}</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
